import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import EmptyData from "components/EmptyData";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/newsocketService";

const navBtnSx = (disabled) => ({
    width: 32, height: 32, borderRadius: "8px",
    border: "1px solid #e0e0e0",
    backgroundColor: disabled ? "#fafafa" : "#fff",
    color: disabled ? "#ccc" : "#555",
    transition: "all 0.15s",
    "&:hover": !disabled ? { backgroundColor: "#f0f4ff", borderColor: "#c5d3f5", color: "#1976d2" } : {},
});

// ── Status Badge ──────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    ACTIVE: { bg: "#E8F5FF", color: "#0057B8", dot: "#2196F3" },
    INACTIVE: { bg: "#FFF0F0", color: "#C0392B", dot: "#F44336" },
    PENDING: { bg: "#FFF8E7", color: "#B76E00", dot: "#F5A623" },
    ONLINE: { bg: "#E8F9F0", color: "#1A7A45", dot: "#4CAF50" },
    OFFLINE: { bg: "#F5F5F5", color: "#666", dot: "#999" },
};

const StatusBadge = ({ status }) => {
    const key = (status || "").toUpperCase();
    const config = STATUS_CONFIG[key] || { bg: "#F5F5F5", color: "#666", dot: "#999" };
    return (
        <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            px: "10px", py: "4px", borderRadius: "20px",
            backgroundColor: config.bg, color: config.color,
            fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px",
            textTransform: "uppercase", whiteSpace: "nowrap",
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: config.dot, flexShrink: 0 }} />
            {status || "N/A"}
        </Box>
    );
};

// ── Custom Pagination Bar ─────────────────────────────────────────────────────
const PaginationBar = ({ currentPage, totalPages, totalCount, limit, onPageChange, onLimitChange }) => {
    const from = totalCount === 0 ? 0 : (currentPage - 1) * limit + 1;
    const to = Math.min(currentPage * limit, totalCount);

    const pageNumbers = useMemo(() => {
        if (totalPages <= 1) return [1];
        const delta = 1;
        const range = [];
        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) range.push(i);
        if (currentPage - delta > 2) range.unshift("...");
        if (currentPage + delta < totalPages - 1) range.push("...");
        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);
        return range;
    }, [currentPage, totalPages]);

    return (
        <Box sx={{
            display: "flex", flexWrap: "wrap",
            alignItems: "center", justifyContent: "space-between",
            gap: 2, mt: "16px", pt: "14px",
            borderTop: "1px solid #f0f0f0", px: 1,
        }}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography sx={{ fontSize: "13px", color: "#666", whiteSpace: "nowrap" }}>
                    Showing{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#333" }}>{from}–{to}</Box>
                    {" "}of{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#333" }}>{totalCount}</Box>
                    {" "}records
                </Typography>
                <Box display="flex" alignItems="center" gap="6px">
                    <Typography sx={{ fontSize: "12px", color: "#999", whiteSpace: "nowrap" }}>
                        Rows per page:
                    </Typography>
                    <FormControl size="small" variant="outlined">
                        <Select
                            value={limit}
                            onChange={(e) => onLimitChange(Number(e.target.value))}
                            sx={{
                                fontSize: "12px", height: "30px", minWidth: "64px", borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#bbb" },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2", borderWidth: "1px" },
                                "& .MuiSelect-select": { py: "4px", px: "10px" },
                            }}
                        >
                            {[5, 10, 20, 50].map((n) => (
                                <MenuItem key={n} value={n} sx={{ fontSize: "12px" }}>{n}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box display="flex" alignItems="center" gap="4px">
                <Tooltip title="First page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === 1} onClick={() => onPageChange(1)} sx={navBtnSx(currentPage === 1)}>
                            <FirstPageIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Previous page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} sx={navBtnSx(currentPage === 1)}>
                            <ChevronLeftIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                {pageNumbers.map((p, i) =>
                    p === "..." ? (
                        <Typography key={`dots-${i}`} sx={{ fontSize: "13px", color: "#ccc", px: "2px", lineHeight: "32px", userSelect: "none" }}>…</Typography>
                    ) : (
                        <Box
                            key={p}
                            onClick={() => p !== currentPage && onPageChange(p)}
                            sx={{
                                width: 32, height: 32,
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                borderRadius: "8px", fontSize: "12px",
                                fontWeight: p === currentPage ? 700 : 500,
                                cursor: p === currentPage ? "default" : "pointer",
                                userSelect: "none", transition: "all 0.15s",
                                backgroundColor: p === currentPage ? "#1976d2" : "transparent",
                                color: p === currentPage ? "#fff" : "#555",
                                border: p === currentPage ? "1.5px solid #1976d2" : "1px solid transparent",
                                boxShadow: p === currentPage ? "0 2px 8px rgba(25,118,210,0.25)" : "none",
                                "&:hover": p !== currentPage ? { backgroundColor: "#f0f4ff", borderColor: "#c5d3f5", color: "#1976d2" } : {},
                            }}
                        >
                            {p}
                        </Box>
                    )
                )}
                <Tooltip title="Next page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(currentPage + 1)} sx={navBtnSx(currentPage === totalPages || totalPages === 0)}>
                            <ChevronRightIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Last page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(totalPages)} sx={navBtnSx(currentPage === totalPages || totalPages === 0)}>
                            <LastPageIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};

const cellSx = {
    padding: "16px 18px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "13px",
    borderBottom: "1px solid #edf2f7",
    verticalAlign: "middle",
};

const Drivers = () => {
    const navigate = useNavigate();
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [lastUpdated, setLastUpdated] = useState(null);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [plans, setPlans] = useState([]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchPlans = useCallback(async () => {
        try {
            const response = await apiClient.get("/driver-plan/active");
            setPlans(Array.isArray(response?.data) ? response.data : []);
        } catch (err) {
            console.error("Failed to load plans", err);
        }
    }, []);

    const fetchDrivers = useCallback(async (pageNum, limitNum, searchStr = "") => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`/driver/all?page=${pageNum}&limit=${limitNum}&search=${encodeURIComponent(searchStr)}`);

            const records = Array.isArray(response)
                ? response
                : Array.isArray(response?.data) ? response.data
                    : Array.isArray(response?.drivers) ? response.drivers
                        : Array.isArray(response?.result) ? response.result
                            : [];

            setDrivers(Array.isArray(records) ? records : []);

            const serverTotal = response?.totalCount ?? response?.total ?? null;
            const serverPages = response?.totalPages ?? null;

            if (serverTotal !== null) {
                setTotalCount(serverTotal);
                setTotalPages(serverPages ?? Math.ceil(serverTotal / limitNum));
            } else {
                setTotalCount(records.length);
                setTotalPages(Math.max(1, Math.ceil(records.length / limitNum)));
            }
            setLastUpdated(new Date().toLocaleString());
        } catch (err) {
            setError(err?.message || "Unable to load drivers.");
            setDrivers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { 
        setPage(1);
        fetchDrivers(1, limit, debouncedSearch); 
    }, [debouncedSearch, limit, fetchDrivers]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    useEffect(() => {
        const handleRefresh = () => {
            fetchDrivers(page, limit, debouncedSearch);
        };
        window.addEventListener("refreshDriversTable", handleRefresh);
        socketService.on("new_driver_registered", handleRefresh);
        socketService.on("driver_registered", handleRefresh);

        return () => {
            window.removeEventListener("refreshDriversTable", handleRefresh);
            socketService.off("new_driver_registered", handleRefresh);
            socketService.off("driver_registered", handleRefresh);
        };
    }, [page, limit, debouncedSearch, fetchDrivers]);

    const handlePageChange = (newPage) => { setPage(newPage); fetchDrivers(newPage, limit, debouncedSearch); };
    const handleLimitChange = (newLimit) => { setLimit(newLimit); setPage(1); fetchDrivers(1, newLimit, debouncedSearch); };

    const handleApproveToggle = async (driverId, currentStatus) => {
        try {
            await apiClient.put(`/driver/approve/${driverId}`, { isApproved: !currentStatus });
            setDrivers((prev) => prev.map((d) => (d._id === driverId) ? { ...d, isApproved: !currentStatus } : d));
        } catch (err) {
            console.error("Failed to update approval status:", err);
            alert("Failed to update driver approval status");
        }
    };

    const handlePlanChange = async (driverId, planId) => {
        try {
            await apiClient.put(`/driver/plan/${driverId}`, { plan: planId });
            setDrivers((prev) => prev.map((d) => {
                if (d._id === driverId) {
                    const newPlan = plans.find(p => p._id === planId) || null;
                    return { ...d, plan: newPlan };
                }
                return d;
            }));
        } catch (err) {
            console.error("Failed to update driver plan:", err);
            alert("Failed to update driver plan");
        }
    };

    const displayCount = totalCount;
    const displayPages = totalPages;
    const pagedDrivers = drivers;

    const columns = [
        { label: "#", width: 70 },
        { label: "Driver Name", width: 220 },
        { label: "Phone", width: 190 },
        { label: "Vehicle", width: 200 },
        { label: "Reg No.", width: 140 },
        { label: "Status", width: 150 },
        { label: "Plan", width: 160 },
        { label: "Approval", width: 120 },
        { label: "Created At", width: 160 },
        { label: "Actions", width: 140 },
    ];
    const tableMinWidth = columns.reduce((s, c) => s + c.width, 0); // 1034px

    return (
        <DashboardLayout>
            <MDBox pt={7} pb={3} sx={{ width: "100%", overflowX: "hidden" }}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card sx={{ overflow: "hidden", width: "100%", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>

                            {/* Header */}
                            <MDBox mx={2} mt={3} py={3} px={2} variant="gradient" bgColor="info"
                                borderRadius="lg" coloredShadow="info"
                                sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <MDTypography variant="h5" color="white" fontWeight="bold">
                                    Driver Management
                                </MDTypography>
                                <MDTypography variant="button" color="white" fontWeight="regular" sx={{ opacity: 0.9 }}>
                                    View all registered drivers and get live driver status from the API.
                                </MDTypography>
                            </MDBox>

                            <Box px={3} py={3} sx={{ width: "100%", overflow: "hidden" }}>

                                {/* Toolbar */}
                                <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
                                    <TextField
                                        label="Search drivers"
                                        placeholder="Search by name, phone, vehicle..."
                                        variant="outlined"
                                        size="small"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ color: "#aaa", fontSize: 18 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ width: 320, minWidth: 220 }}
                                    />
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography variant="caption" color="textSecondary">
                                            {lastUpdated ? `Last updated: ${lastUpdated}` : "Not loaded yet"}
                                        </Typography>
                                        <MDButton variant="gradient" color="info" size="small" onClick={() => { fetchDrivers(page, limit, debouncedSearch); fetchPlans(); }}>
                                            Refresh
                                        </MDButton>
                                    </Box>
                                </Box>

                                {/* States */}
                                {loading ? (
                                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={240} gap={2}>
                                        <Box sx={{
                                            width: 40, height: 40, border: "3px solid #e0e0e0",
                                            borderTop: "3px solid #1976d2", borderRadius: "50%",
                                            animation: "spin 0.8s linear infinite",
                                            "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
                                        }} />
                                        <Typography sx={{ color: "#aaa", fontSize: "14px" }}>Loading drivers...</Typography>
                                    </Box>
                                ) : error ? (
                                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={240} gap={2}>
                                        <Typography color="error" sx={{ fontSize: "14px" }}>{error}</Typography>
                                        <MDButton variant="gradient" color="success" size="small" onClick={() => fetchDrivers(page, limit)}>
                                            Retry
                                        </MDButton>
                                    </Box>
                                ) : pagedDrivers.length === 0 ? (
                                    <EmptyData />
                                ) : (
                                    <>
                                        <TableContainer
                                            component={MDBox}
                                            sx={{
                                                width: "100%",
                                                overflowX: "auto",
                                                borderRadius: "18px",
                                                border: "1px solid #e9eef5",
                                                backgroundColor: "#fff",
                                            }}
                                        >
                                            <Table
                                                stickyHeader
                                                size="medium"
                                                sx={{
                                                    minWidth: tableMinWidth,
                                                    tableLayout: "fixed",
                                                    borderCollapse: "separate",
                                                    borderSpacing: 0,
                                                    width: "100%",
                                                }}
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        {columns.map((col) => (
                                                            <TableCell
                                                                key={col.label}
                                                                align="left"
                                                                sx={{
                                                                    background: "#f8fafc",
                                                                    color: "#64748b",
                                                                    fontWeight: 700,
                                                                    fontSize: "12px",
                                                                    textTransform: "uppercase",
                                                                    letterSpacing: "0.6px",
                                                                    padding: "18px",
                                                                    borderBottom: "1px solid #e2e8f0",
                                                                    width: col.width,
                                                                    minWidth: col.width,
                                                                }}
                                                            >
                                                                {col.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {pagedDrivers.map((driver, index) => (
                                                        <TableRow
                                                            key={driver._id || index}
                                                            hover
                                                            sx={{
                                                                transition: "background 0.15s",
                                                                "&:hover": { backgroundColor: "#f7fbff" },
                                                                "&:nth-of-type(even)": { backgroundColor: "#fafbfc" },
                                                                "&:nth-of-type(even):hover": { backgroundColor: "#f7fbff" },
                                                            }}
                                                        >
                                                            {/* # */}
                                                            <TableCell sx={{ ...cellSx, maxWidth: "none", overflow: "visible" }}>
                                                                <Box sx={{
                                                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                                    width: 26, height: 26, borderRadius: "50%",
                                                                    backgroundColor: "#f0f4ff", color: "#5c7cfa",
                                                                    fontSize: "11px", fontWeight: 700, flexShrink: 0,
                                                                }}>
                                                                    {(page - 1) * limit + index + 1}
                                                                </Box>
                                                            </TableCell>

                                                            {/* Driver Name */}
                                                            <TableCell sx={cellSx} title={driver.driverName || driver.DriverName || driver.name || "N/A"}>
                                                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                    {driver.driverName || driver.DriverName || driver.name || "N/A"}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* Phone */}
                                                            <TableCell sx={cellSx}>
                                                                <Typography sx={{ fontSize: "13px", color: "#444", whiteSpace: "nowrap" }}>
                                                                    {driver.phoneNumber || driver.DriverMobile || driver.driverPhone || "N/A"}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* Vehicle */}
                                                            <TableCell sx={{ ...cellSx, maxWidth: "none", overflow: "visible" }}>
                                                                <Box sx={{
                                                                    display: "inline-block",
                                                                    px: 1.5, py: "3px",
                                                                    borderRadius: "8px",
                                                                    backgroundColor: "#f0f4ff",
                                                                    color: "#3d5af1",
                                                                    fontSize: "12px", fontWeight: 600,
                                                                    whiteSpace: "nowrap",
                                                                    maxWidth: "140px",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }}
                                                                    title={driver.vehicleType || "N/A"}
                                                                >
                                                                    {driver.vehicleType || "N/A"}
                                                                </Box>
                                                            </TableCell>

                                                            {/* Reg No */}
                                                            <TableCell sx={cellSx}>
                                                                <Typography sx={{
                                                                    fontSize: "12px", fontFamily: "monospace", color: "#555",
                                                                    backgroundColor: "#f5f5f5", px: "6px", py: "2px",
                                                                    borderRadius: "6px", display: "inline-block",
                                                                    whiteSpace: "nowrap",
                                                                }}>
                                                                    {driver.regNo || "N/A"}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* Status */}
                                                            <TableCell sx={{ ...cellSx, maxWidth: "none", overflow: "visible" }}>
                                                                <StatusBadge status={driver.status || driver.Status} />
                                                            </TableCell>

                                                            {/* Plan */}
                                                            <TableCell sx={{ ...cellSx, maxWidth: "none", overflow: "visible" }}>
                                                                <FormControl size="small" variant="outlined" sx={{ width: "100%" }}>
                                                                    <Select
                                                                        value={driver.plan?._id || driver.plan || ""}
                                                                        displayEmpty
                                                                        onChange={(e) => handlePlanChange(driver._id, e.target.value)}
                                                                        sx={{
                                                                            fontSize: "12px", height: "30px", borderRadius: "6px",
                                                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                                                                            "& .MuiSelect-select": { py: "4px", px: "8px" },
                                                                        }}
                                                                    >
                                                                        <MenuItem value="" sx={{ fontSize: "12px", color: "#aaa" }}>
                                                                            <em>No Plan</em>
                                                                        </MenuItem>
                                                                        {plans.map((p) => (
                                                                            <MenuItem key={p._id} value={p._id} sx={{ fontSize: "12px" }}>
                                                                                {p.name || p.title || "Plan"}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>

                                                            {/* Approval */}
                                                            <TableCell sx={cellSx}>
                                                                <MDButton
                                                                    variant="gradient"
                                                                    color={driver.isApproved ? "success" : "warning"}
                                                                    size="small"
                                                                    onClick={() => handleApproveToggle(driver._id, driver.isApproved)}
                                                                >
                                                                    {driver.isApproved ? "Approved" : "Approve"}
                                                                </MDButton>
                                                            </TableCell>

                                                            {/* Created At */}
                                                            <TableCell sx={cellSx}>
                                                                <Typography sx={{ fontSize: "12px", color: "#666", whiteSpace: "nowrap" }}>
                                                                    {driver.createdAt
                                                                        ? new Date(driver.createdAt).toLocaleDateString("en-GB", {
                                                                            day: "2-digit", month: "short", year: "numeric",
                                                                        })
                                                                        : "N/A"}
                                                                </Typography>
                                                            </TableCell>
                                                            
                                                            {/* Actions */}
                                                            <TableCell sx={cellSx}>
                                                                <MDButton
                                                                    variant="outlined"
                                                                    color="info"
                                                                    size="small"
                                                                    onClick={() => navigate(`/drivers/trips/${driver._id}`)}
                                                                >
                                                                    View Trips
                                                                </MDButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <PaginationBar
                                            currentPage={page}
                                            totalPages={displayPages}
                                            totalCount={displayCount}
                                            limit={limit}
                                            onPageChange={handlePageChange}
                                            onLimitChange={handleLimitChange}
                                        />
                                    </>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default Drivers;